import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchJson, postJson } from './adminApi';
import { duration, formatDateTime } from './adminFormat';
import {
    LOAD_METRICS, SCENARIOS, VIEWS, isCancelled, pickFrameSize, restoreLeftoverState, runMapPerf,
} from './mapPerfRun';
import {
    BASELINE_RUNS, ChangeChart, Delta, LOAD_FLOOR, LoadTimeline, METRICS, SIGNIFICANT, SmoothnessChart, TrendGrid,
    baselineRuns, compare, num, runTime, shortCommit, valueOf,
} from './mapPerfCharts';

// "Map speed": one button runs the map speed test (mapPerfRun.js) in this browser and stores the run on the backend
// (GET/POST /admin/perf/map-runs), so the numbers can be followed over time and a new feature that slows the map
// shows up against the runs before it. Runs are only compared with runs from the same device (machine, screen,
// browser, build kind), as frame times depend on all of them.

const RUNS_PATH = '/admin/perf/map-runs';
const PENDING_KEY = 'map_perf_pending_v1';
// What the verdict line names (p95 and the rest are marked in the table; alone they are noisier).
const HEADLINE = ['avg', 'longTaskMs'];
const VERDICT_MAX = 6;
const STEADY_BASELINE = 3;

const readPending = () => {
    try {
        return JSON.parse(window.localStorage.getItem(PENDING_KEY) || '[]') || [];
    } catch (error) {
        return [];
    }
};
const writePending = (runs) => {
    try {
        if (runs.length) window.localStorage.setItem(PENDING_KEY, JSON.stringify(runs));
        else window.localStorage.removeItem(PENDING_KEY);
    } catch (error) {
        // Storage blocked: an unsaved run is lost with the tab.
    }
};

const MapPerfTab = () => {
    const [runs, setRuns] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [pending, setPending] = useState(readPending);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(null);
    const [runError, setRunError] = useState('');
    const [frameSize, setFrameSize] = useState(pickFrameSize);
    const [deviceKey, setDeviceKey] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const frameRef = useRef(null);
    const abortRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const body = await fetchJson(RUNS_PATH);
            setRuns(Array.isArray(body.runs) ? body.runs : []);
            setLoadError('');
        } catch (error) {
            setLoadError(error.message);
        }
    }, []);

    const uploadPending = useCallback(async () => {
        const left = [];
        for (const run of readPending()) {
            try {
                await postJson(RUNS_PATH, run);
            } catch (error) {
                left.push(run);
            }
        }
        writePending(left);
        setPending(left);
        return left;
    }, []);

    useEffect(() => {
        restoreLeftoverState();
        uploadPending().finally(load);
    }, [load, uploadPending]);

    const allRuns = useMemo(() => [...runs, ...pending.map((run) => ({ ...run, pending: true }))]
        .sort((a, b) => runTime(a).localeCompare(runTime(b))), [runs, pending]);
    const devices = useMemo(() => {
        const found = new Map();
        allRuns.forEach((run) => {
            const key = run.device?.key;
            if (!key) return;
            const entry = found.get(key) || { key, label: run.device.label || key, count: 0 };
            entry.count += 1;
            found.set(key, entry);
        });
        return [...found.values()];
    }, [allRuns]);
    const activeDevice = deviceKey || allRuns[allRuns.length - 1]?.device?.key || '';
    const deviceRuns = useMemo(() => allRuns.filter((run) => run.device?.key === activeDevice), [allRuns, activeDevice]);
    const selected = deviceRuns.find((run) => (run.id ?? run.at) === selectedId) || deviceRuns[deviceRuns.length - 1] || null;
    const baseline = useMemo(() => (selected ? baselineRuns(selected, allRuns) : []), [selected, allRuns]);

    const start = async () => {
        setRunError('');
        setFrameSize(pickFrameSize());
        setRunning(true);
        setProgress({ view: '', step: 'starting', done: 0, total: 1 });
        const controller = new AbortController();
        abortRef.current = controller;
        // The frame is rendered with the overlay; wait for it.
        await new Promise((resolve) => { requestAnimationFrame(() => requestAnimationFrame(resolve)); });
        try {
            const run = await runMapPerf({ frame: frameRef.current, onProgress: setProgress, signal: controller.signal });
            let saved = null;
            try {
                saved = await postJson(RUNS_PATH, run);
            } catch (error) {
                const left = [...readPending(), run];
                writePending(left);
                setPending(left);
                setRunError(`The run couldn't be saved on the server (${error.message}); it's kept in this browser and sent next time.`);
            }
            await load();
            setDeviceKey(run.device.key);
            setSelectedId(saved?.id ?? run.at);
        } catch (error) {
            if (!isCancelled(error)) setRunError(error.message || String(error));
        } finally {
            setRunning(false);
            setProgress(null);
            abortRef.current = null;
        }
    };

    const remove = async (run) => {
        if (run.pending) {
            const left = readPending().filter((item) => item.at !== run.at);
            writePending(left);
            setPending(left);
            return;
        }
        try {
            await postJson(`${RUNS_PATH}/${run.id}/delete`, {});
            await load();
        } catch (error) {
            setRunError(error.message);
        }
    };

    // The headline changes of the selected run against the earlier runs on its device.
    const changes = useMemo(() => {
        if (!selected || !baseline.length) return [];
        const list = [];
        VIEWS.forEach((view) => {
            SCENARIOS.forEach((scenario) => {
                HEADLINE.forEach((metricId) => {
                    const metric = METRICS.find((item) => item.id === metricId);
                    const result = compare(valueOf(selected, view.id, scenario.id, metricId),
                        baseline.map((run) => valueOf(run, view.id, scenario.id, metricId)), metric);
                    if (result?.direction) list.push({ view, scenario, metric, result });
                });
            });
        });
        return list;
    }, [selected, baseline]);
    const byRatio = (a, b) => Math.abs(b.result.ratio) - Math.abs(a.result.ratio);
    const worse = changes.filter((change) => change.result.direction === 'worse').sort(byRatio);
    const better = changes.filter((change) => change.result.direction === 'better').sort(byRatio);
    const describe = (list) => {
        const named = list.slice(0, VERDICT_MAX).map(({ view, scenario, metric, result }) => (
            `${view.label} ${scenario.label.toLowerCase()} ${metric.label.toLowerCase()} ${result.ratio > 0 ? '+' : ''}${Math.round(result.ratio * 100)} %`
        ));
        return list.length > VERDICT_MAX ? `${named.join(', ')} and ${list.length - VERDICT_MAX} more` : named.join(', ');
    };
    const earlier = `the ${baseline.length > 1 ? `${baseline.length} runs` : 'run'} before`;

    const pct = progress ? Math.round((100 * progress.done) / Math.max(1, progress.total)) : 0;

    return (
        <>
            <section className="ax-card">
                <h3>Map speed test</h3>
                <p className="ax-mp-intro">
                    Opens kartvy and then listvy with Stockholms län picked and no filters, and measures how long the
                    map takes to load and how smoothly it zooms, pans (zoomed out and close), flies and reacts to the
                    pointer. It takes three to four minutes: keep this tab in front. Each run is saved, and compared with
                    the {BASELINE_RUNS} runs before it on the same device; a change of {Math.round(SIGNIFICANT * 100)} % or more
                    is marked.
                </p>
                {process.env.NODE_ENV !== 'production' && (
                    <p className="ax-warning">
                        This is the dev server: React&apos;s development build is several times slower than the live
                        site. Its runs are only compared with other dev runs.
                    </p>
                )}
                <div className="ax-actions">
                    <button type="button" className="ax-button" onClick={start} disabled={running}>Run test</button>
                    {pending.length > 0 && (
                        <button type="button" className="ax-link" onClick={() => uploadPending().then(load)}>
                            Send {pending.length} unsaved run{pending.length > 1 ? 's' : ''} to the server
                        </button>
                    )}
                </div>
                {runError && <p className="ax-error">{runError}</p>}
                {loadError && <p className="ax-error">Couldn&apos;t load the earlier runs: {loadError}</p>}
            </section>

            {selected && (
                <section className="ax-card">
                    <div className="ax-mp-head">
                        <h3>
                            {formatDateTime(runTime(selected))}
                            <span className="ax-muted"> · {shortCommit(selected)} {selected.build?.message || ''}</span>
                        </h3>
                        {devices.length > 0 && (
                            <label className="ax-field ax-mp-device">
                                <span>Device</span>
                                <select value={activeDevice} onChange={(event) => { setDeviceKey(event.target.value); setSelectedId(null); }}>
                                    {devices.map((device) => (
                                        <option key={device.key} value={device.key}>{device.label} ({device.count})</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    <p className="ax-muted ax-mp-device-line">{selected.device?.label} · {selected.env} build · {duration(selected.durationS)}</p>
                    {selected.warnings?.map((warning) => <p key={warning} className="ax-warning">{warning}</p>)}
                    {!baseline.length && <p className="ax-muted">No earlier runs on this device to compare with yet.</p>}
                    {baseline.length > 0 && (
                        <p className={`ax-mp-verdict${worse.length ? ' is-worse' : ''}`}>
                            {worse.length ? `Slower than ${earlier}: ${describe(worse)}.` : `No significant slowdown against ${earlier}.`}
                            {better.length > 0 && ` Faster: ${describe(better)}.`}
                            {baseline.length < STEADY_BASELINE && (
                                <span className="ax-muted"> With fewer than {STEADY_BASELINE} earlier runs the comparison is rough: frame times vary a little from run to run.</span>
                            )}
                        </p>
                    )}
                    <h4 className="ax-mp-sub">How smooth it was <span className="ax-muted">— every frame drawn in each scenario</span></h4>
                    <SmoothnessChart run={selected} baseline={baseline} />
                    {baseline.length > 0 && (
                        <>
                            <h4 className="ax-mp-sub">Against earlier runs</h4>
                            <ChangeChart run={selected} baseline={baseline} />
                        </>
                    )}
                    <h4 className="ax-mp-sub">Loading <span className="ax-muted">— from opening the view; depends on the network and caches</span></h4>
                    <LoadTimeline run={selected} baseline={baseline} />
                    <details className="ax-mp-details">
                        <summary>All the numbers</summary>
                    <div className="ax-table-scroll">
                        <table className="ax-table ax-mp-table">
                            <thead>
                                <tr>
                                    <th>View</th>
                                    <th>Scenario</th>
                                    {METRICS.map((metric) => <th key={metric.id} className="num">{metric.label}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {VIEWS.map((view) => SCENARIOS.map((scenario, index) => (
                                    <tr key={`${view.id}-${scenario.id}`} className={index === 0 ? 'is-first' : ''}>
                                        <td>{index === 0 ? view.label : ''}</td>
                                        <td title={scenario.hint}>{scenario.label}</td>
                                        {METRICS.map((metric) => {
                                            const value = valueOf(selected, view.id, scenario.id, metric.id);
                                            const result = compare(value, baseline.map((run) => valueOf(run, view.id, scenario.id, metric.id)), metric);
                                            return (
                                                <td key={metric.id} className="num">
                                                    {num(value, metric.unit, metric.int)}
                                                    <Delta result={result} />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )))}
                            </tbody>
                        </table>
                    </div>
                    <div className="ax-table-scroll">
                        <table className="ax-table ax-mp-table">
                            <thead>
                                <tr>
                                    <th>View</th>
                                    {LOAD_METRICS.map((metric) => <th key={metric.id} className="num">{metric.label}</th>)}
                                    <th className="num">Markers at zoom 14</th>
                                </tr>
                            </thead>
                            <tbody>
                                {VIEWS.map((view) => (
                                    <tr key={view.id}>
                                        <td>{view.label}</td>
                                        {LOAD_METRICS.map((metric) => {
                                            const value = selected.results?.[view.id]?.load?.[metric.id];
                                            const result = compare(value, baseline.map((run) => run.results?.[view.id]?.load?.[metric.id]), { floor: LOAD_FLOOR });
                                            return (
                                                <td key={metric.id} className="num">
                                                    {num(value, 'ms')}
                                                    <Delta result={result} />
                                                </td>
                                            );
                                        })}
                                        <td className="num">{num(selected.results?.[view.id]?.markers, '', true)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </details>
                </section>
            )}

            {deviceRuns.length > 0 && (
                <section className="ax-card">
                    <h3>Over time <span className="ax-muted">— the runs on this device, oldest on the left; point at one to see it in every chart</span></h3>
                    <TrendGrid runs={deviceRuns} allRuns={allRuns} />
                </section>
            )}

            {deviceRuns.length > 0 && (
                <section className="ax-card ax-table-card">
                    <div className="ax-table-meta"><span>Runs on this device</span><span className="ax-muted">avg frame time, ms</span></div>
                    <div className="ax-table-scroll">
                        <table className="ax-table">
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Build</th>
                                    {VIEWS.map((view) => ['zoom', 'pan14', 'hover'].map((scenario) => (
                                        <th key={`${view.id}-${scenario}`} className="num">{view.label} {SCENARIOS.find((item) => item.id === scenario).label.toLowerCase()}</th>
                                    )))}
                                    <th aria-label="actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {[...deviceRuns].reverse().map((run) => (
                                    <tr key={run.id ?? run.at} className={run === selected ? 'is-selected' : ''}>
                                        <td>
                                            <button type="button" className="ax-link" onClick={() => setSelectedId(run.id ?? run.at)}>
                                                {formatDateTime(runTime(run))}
                                            </button>
                                            {run.invalid && <span className="ax-pill ax-mp-flag" title={(run.warnings || []).join(' ')}>not comparable</span>}
                                            {run.pending && <span className="ax-pill ax-mp-flag">not saved</span>}
                                        </td>
                                        <td className="ax-mp-build" title={run.build?.message || ''}>
                                            <code>{shortCommit(run)}</code> {run.build?.message || ''}
                                        </td>
                                        {VIEWS.map((view) => ['zoom', 'pan14', 'hover'].map((scenario) => (
                                            <td key={`${view.id}-${scenario}`} className="num">{num(valueOf(run, view.id, scenario, 'avg'))}</td>
                                        )))}
                                        <td className="num">
                                            <button type="button" className="ax-link" onClick={() => remove(run)} disabled={running}>delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {running && createPortal(
                <div className="map-perf-overlay" role="dialog" aria-modal="true" aria-label="Map speed test running">
                    <div className="map-perf-bar">
                        <strong>Map speed test</strong>
                        <span className="map-perf-step">{progress?.view ? `${progress.view}: ` : ''}{progress?.step}</span>
                        <span className="map-perf-progress" aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>
                        <span className="map-perf-hint">Keep this tab in front</span>
                        <button type="button" onClick={() => abortRef.current?.abort()}>Cancel</button>
                    </div>
                    <iframe
                        ref={frameRef}
                        className="map-perf-frame"
                        title="The map under test"
                        style={{ width: frameSize.width, height: frameSize.height }}
                    />
                </div>,
                document.body,
            )}
        </>
    );
};

export default MapPerfTab;
